(() => {
    'use strict';

    const DRAWER_ID = 'CartDrawer';
    const TOGGLE_SELECTOR = '[data-cart-drawer-toggle]';
    const QUANTITY_DEBOUNCE = 300;

    let cartDrawerInstance = null;

    const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const getFocusableElements = (container) => {
        if (!container) {
            return [];
        }

        return [...container.querySelectorAll(focusableSelector)].filter((element) => {
            if (element.closest('[hidden]') || element.hasAttribute('inert')) {
                return false;
            }

            return Boolean(
                element.offsetWidth ||
                element.offsetHeight ||
                element.getClientRects().length
            );
        });
    };

    const parseTime = (value) => {
        const trimmed = value.trim();

        if (trimmed.endsWith('ms')) {
            return Number.parseFloat(trimmed) || 0;
        }

        if (trimmed.endsWith('s')) {
            return (Number.parseFloat(trimmed) || 0) * 1000;
        }

        return 0;
    };

    const getTransitionDuration = (element) => {
        if (!element) {
            return 0;
        }

        const styles = window.getComputedStyle(element);
        const durations = styles.transitionDuration.split(',').map(parseTime);
        const delays = styles.transitionDelay.split(',').map(parseTime);
        const count = Math.max(durations.length, delays.length);

        let longest = 0;

        for (let index = 0; index < count; index += 1) {
            const duration = durations[index % durations.length] || 0;
            const delay = delays[index % delays.length] || 0;

            longest = Math.max(
                longest,
                duration + delay
            );
        }

        return longest;
    };

    class CartDrawer {
        constructor() {
            this.root = document.getElementById(DRAWER_ID);

            if (!this.root) {
                return;
            }

            this.panel = this.root.querySelector(
                '[data-cart-drawer-panel]'
            );

            this.backdrop = this.root.querySelector(
                '[data-cart-drawer-backdrop]'
            );

            this.closeButton = this.root.querySelector(
                '[data-cart-drawer-close]'
            );

            this.container = this.root.querySelector(
                '[data-cart-drawer-container]'
            );

            this.feedback = this.root.querySelector(
                '[data-cart-feedback]'
            );

            this.abortController = new AbortController();
            this.containerAbortController = null;
            this.activeRefreshController = null;
            this.activeMutationControllers = new Set();

            this.quantityTimeouts = new Map();

            this.refreshSequence = 0;
            this.closeTimer = null;

            this.lastFocusedElement = null;

            this.inertElements = [];

            this.init();
        }

        /* ==========================================================
           Initialization
           ========================================================== */

        init() {
            if (!this.root) {
                return;
            }

            const signal = this.abortController.signal;

            document.addEventListener(
                'click',
                (event) => this.handleToggleClick(event),
                { signal }
            );

            this.backdrop?.addEventListener(
                'click',
                () => this.close(),
                { signal }
            );

            this.closeButton?.addEventListener(
                'click',
                () => this.close(),
                { signal }
            );

            document.addEventListener(
                'keydown',
                (event) => this.handleKeydown(event),
                { signal }
            );

            /*
             * Existing product / quick-add / cart-page files already dispatch
             * these events. Keep them as the compatibility contract while the
             * visual theme is redesigned.
             */
            document.addEventListener(
                'theme:cart:add',
                (event) => this.handleCartAdd(event),
                { signal }
            );

            document.addEventListener(
                'theme:cart:update',
                (event) => this.handleCartUpdate(event),
                { signal }
            );

            document.addEventListener(
                'theme:cart:error',
                (event) => this.handleCartError(event),
                { signal }
            );

            this.bindContainer(this.container);

            this.prepareTriggers();

            this.updateBadges(
                Number(this.root.dataset.itemCount) || 0
            );
        }

        bindContainer(container) {
            this.containerAbortController?.abort();

            this.containerAbortController =
                new AbortController();

            if (!container) {
                return;
            }

            const signal =
                this.containerAbortController.signal;

            container.addEventListener(
                'click',
                (event) => this.handleContainerClick(event),
                { signal }
            );

            container.addEventListener(
                'change',
                (event) => this.handleContainerChange(event),
                { signal }
            );
        }

        prepareTriggers() {
            document
                .querySelectorAll(TOGGLE_SELECTOR)
                .forEach((trigger) => {
                    trigger.setAttribute(
                        'aria-controls',
                        DRAWER_ID
                    );

                    trigger.setAttribute(
                        'aria-expanded',
                        String(this.isOpen())
                    );
                });
        }

        /* ==========================================================
           Routes
           ========================================================== */

        getShopRoot() {
            const root =
                window.Shopify?.routes?.root ||
                '/';

            return root.endsWith('/')
                ? root
                : `${root}/`;
        }

        /* ==========================================================
           Open / close
           ========================================================== */

        handleToggleClick(event) {
            const trigger =
                event.target.closest(
                    TOGGLE_SELECTOR
                );

            if (!trigger) {
                return;
            }

            event.preventDefault();

            if (this.isOpen()) {
                this.close();
                return;
            }

            this.open(trigger);
        }

        isOpen() {
            return Boolean(
                this.root?.classList.contains(
                    'is-open'
                )
            );
        }

        closeOtherOverlays() {
            const searchModal =
                document.querySelector(
                    '[data-search-modal].is-open'
                );

            if (searchModal) {
                searchModal
                    .querySelector(
                        '[data-search-modal-close]'
                    )
                    ?.click();
            }

            const mobileHeaderMenu =
                document.querySelector(
                    '[data-header-mobile-menu].is-open'
                );

            if (mobileHeaderMenu) {
                mobileHeaderMenu
                    .querySelector(
                        '.header__mobile-close'
                    )
                    ?.click();
            }
        }

        open(trigger = null) {
            if (
                !this.root ||
                this.isOpen()
            ) {
                return;
            }

            this.closeOtherOverlays();

            window.clearTimeout(
                this.closeTimer
            );

            this.lastFocusedElement =
                trigger instanceof HTMLElement
                    ? trigger
                    : (
                        document.activeElement instanceof HTMLElement
                            ? document.activeElement
                            : null
                    );

            this.root.hidden = false;

            this.setTriggerExpanded(true);

            this.setBackgroundInert(true);

            requestAnimationFrame(() => {
                this.root.classList.add(
                    'is-open'
                );

                const reducedMotion =
                    window.matchMedia(
                        '(prefers-reduced-motion: reduce)'
                    ).matches;

                window.setTimeout(
                    () => {
                        this.closeButton?.focus({
                            preventScroll: true
                        });
                    },
                    reducedMotion
                        ? 0
                        : 60
                );
            });

            document.dispatchEvent(
                new CustomEvent(
                    'theme:cart-drawer:open'
                )
            );
        }

        close({
            restoreFocus = true,
            immediate = false
        } = {}) {
            if (
                !this.root ||
                !this.isOpen()
            ) {
                return;
            }

            window.clearTimeout(
                this.closeTimer
            );

            this.root.classList.remove(
                'is-open'
            );

            this.setTriggerExpanded(false);

            const reducedMotion =
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches;

            if (
                immediate ||
                reducedMotion
            ) {
                this.finishClose(
                    restoreFocus
                );

                return;
            }

            const duration =
                Math.max(
                    getTransitionDuration(
                        this.panel
                    ),
                    getTransitionDuration(
                        this.backdrop
                    )
                );

            this.closeTimer =
                window.setTimeout(
                    () => {
                        this.finishClose(
                            restoreFocus
                        );
                    },
                    duration + 20
                );
        }

        finishClose(
            restoreFocus = true
        ) {
            if (!this.root) {
                return;
            }

            this.root.hidden = true;

            this.setBackgroundInert(false);

            if (
                restoreFocus &&
                this.lastFocusedElement?.isConnected &&
                typeof this.lastFocusedElement.focus ===
                'function'
            ) {
                this.lastFocusedElement.focus();
            }

            this.lastFocusedElement = null;

            document.dispatchEvent(
                new CustomEvent(
                    'theme:cart-drawer:close'
                )
            );
        }

        setTriggerExpanded(expanded) {
            document
                .querySelectorAll(
                    TOGGLE_SELECTOR
                )
                .forEach((trigger) => {
                    trigger.setAttribute(
                        'aria-expanded',
                        String(expanded)
                    );
                });
        }

        setBackgroundInert(
            shouldInert
        ) {
            if (
                !(
                    'inert' in
                    HTMLElement.prototype
                )
            ) {
                return;
            }

            if (shouldInert) {
                this.inertElements = [];

                const owner =
                    Array.from(
                        document.body.children
                    ).find((child) =>
                        child.contains(this.root)
                    );

                Array.from(
                    document.body.children
                ).forEach((child) => {
                    if (
                        child === owner ||
                        child.inert
                    ) {
                        return;
                    }

                    this.inertElements.push({
                        element: child,
                        previous: child.inert
                    });

                    child.inert = true;
                });

                return;
            }

            this.inertElements.forEach(
                ({
                    element,
                    previous
                }) => {
                    if (element.isConnected) {
                        element.inert =
                            previous;
                    }
                }
            );

            this.inertElements = [];
        }

        /* ==========================================================
           Existing cart events
           ========================================================== */

        handleCartAdd(event) {
            const source =
                event.detail?.source;

            /*
             * Browsing-context additions keep the shopper where they are.
             * Their local UI confirms the add while the cart silently syncs.
             */
            if (
                source === 'quick-add' ||
                source === 'featured-product'
            ) {
                this.refreshSection();

                return;
            }

            this.open();

            this.refreshSection();
        }

        handleCartUpdate(event) {
            const cart =
                event.detail?.cart;

            if (
                typeof cart?.item_count ===
                'number'
            ) {
                this.updateBadges(
                    cart.item_count
                );
            }

            if (this.isOpen()) {
                this.refreshSection();
            }
        }

        handleCartError(event) {
            const message =
                event.detail?.error?.description ||
                event.detail?.error?.message ||
                this.root?.dataset.cartGeneralError || '';

            if (this.isOpen()) {
                this.showFeedback(
                    message
                );
            }
        }

        /* ==========================================================
           Quantity / remove controls
           ========================================================== */

        handleContainerClick(event) {
            const decrement =
                event.target.closest(
                    '[data-cart-quantity-decrement]'
                );

            if (decrement) {
                this.step(
                    decrement,
                    -1
                );

                return;
            }

            const increment =
                event.target.closest(
                    '[data-cart-quantity-increment]'
                );

            if (increment) {
                this.step(
                    increment,
                    1
                );

                return;
            }

            const remove =
                event.target.closest(
                    '[data-cart-remove]'
                );

            if (remove) {
                this.removeLine(
                    remove
                );
            }
        }

        handleContainerChange(event) {
            const input =
                event.target.closest(
                    '[data-cart-quantity-input]'
                );

            if (!input) {
                return;
            }

            const lineItem =
                input.closest(
                    '[data-cart-line-item]'
                );

            const min =
                Number(input.min) || 0;

            const max =
                Number(input.max) ||
                Infinity;

            const value =
                Number(input.value);

            const clamped =
                this.clamp(
                    Number.isFinite(value)
                        ? value
                        : min,
                    min,
                    max
                );

            input.value =
                String(clamped);

            this.commitQuantity(
                lineItem,
                clamped
            );
        }

        step(button, direction) {
            const lineItem =
                button.closest(
                    '[data-cart-line-item]'
                );

            const input =
                lineItem?.querySelector(
                    '[data-cart-quantity-input]'
                );

            if (!input) {
                return;
            }

            const stepValue =
                Number(input.step) || 1;

            const min =
                Number(input.min) || 0;

            const max =
                Number(input.max) ||
                Infinity;

            const current =
                Number(input.value) || 0;

            const next =
                this.clamp(
                    current +
                    direction * stepValue,
                    min,
                    max
                );

            if (next === current) {
                return;
            }

            input.value =
                String(next);

            this.commitQuantity(
                lineItem,
                next
            );
        }

        clamp(value, min, max) {
            return Math.min(
                max,
                Math.max(
                    min,
                    value
                )
            );
        }

        commitQuantity(
            lineItem,
            quantity,
            { immediate = false } = {}
        ) {
            const line =
                Number(
                    lineItem?.dataset.line
                );

            if (!Number.isFinite(line)) {
                return;
            }

            const existing =
                this.quantityTimeouts.get(
                    line
                );

            if (existing) {
                window.clearTimeout(
                    existing
                );
            }

            const run = () => {
                this.quantityTimeouts.delete(
                    line
                );

                this.changeLine(
                    { line },
                    quantity
                );
            };

            if (immediate) {
                run();

                return;
            }

            this.quantityTimeouts.set(
                line,
                window.setTimeout(
                    run,
                    QUANTITY_DEBOUNCE
                )
            );
        }

        removeLine(button) {
            const lineItem =
                button.closest(
                    '[data-cart-line-item]'
                );

            if (!lineItem) {
                return;
            }

            const line =
                Number(
                    lineItem.dataset.line
                );

            const key =
                lineItem.dataset.key || '';

            if (
                !key &&
                !Number.isFinite(line)
            ) {
                return;
            }

            const input =
                lineItem.querySelector(
                    '[data-cart-quantity-input]'
                );

            const removedQuantity =
                Math.max(
                    1,
                    Number(
                        input?.value
                    ) || 1
                );

            this.optimisticallyRemoveLine(
                lineItem,
                removedQuantity
            );

            this.changeLine(
                {
                    line,
                    key
                },
                0,
                {
                    optimistic: true
                }
            );
        }

        optimisticallyRemoveLine(
            lineItem,
            removedQuantity
        ) {
            const lineItems =
                [
                    ...this.container.querySelectorAll(
                        '[data-cart-line-item]'
                    )
                ];

            const index =
                lineItems.indexOf(
                    lineItem
                );

            const sibling =
                lineItems[index + 1] ||
                lineItems[index - 1] ||
                null;

            const focusTarget =
                sibling?.querySelector(
                    '[data-cart-remove]'
                ) ||
                this.closeButton;

            lineItem.remove();

            const currentCount =
                Number(
                    this.root.dataset.itemCount
                );

            if (
                Number.isFinite(
                    currentCount
                )
            ) {
                this.updateBadges(
                    Math.max(
                        0,
                        currentCount -
                        removedQuantity
                    )
                );
            }

            const remainingItems =
                this.container.querySelectorAll(
                    '[data-cart-line-item]'
                );

            if (
                !remainingItems.length
            ) {
                this.showOptimisticEmptyState();
            }

            requestAnimationFrame(() => {
                if (
                    focusTarget?.isConnected
                ) {
                    focusTarget.focus({
                        preventScroll: true
                    });
                }
            });
        }

        showOptimisticEmptyState() {
            this.container
                .querySelector(
                    '[data-cart-line-items]'
                )
                ?.remove();

            this.container
                .querySelector(
                    '.cart-drawer__summary'
                )
                ?.remove();

            this.container
                .querySelector(
                    '.cart-drawer__footer'
                )
                ?.remove();

            if (
                this.container.querySelector(
                    '[data-cart-empty]'
                )
            ) {
                return;
            }

            const empty =
                document.createElement(
                    'div'
                );

            empty.className =
                'cart-drawer__empty';

            empty.setAttribute(
                'data-cart-empty',
                ''
            );

            empty.setAttribute(
                'data-optimistic-cart-empty',
                ''
            );

            const copy =
                document.createElement(
                    'div'
                );

            copy.className =
                'cart-drawer__empty-copy';

            const title =
                document.createElement(
                    'h3'
                );

            title.className =
                'cart-drawer__empty-title';

            title.textContent =
                'Your bag is empty';

            const message =
                document.createElement(
                    'p'
                );

            message.className =
                'cart-drawer__empty-message';

            message.textContent =
                'Add something you like and it will appear here.';

            copy.append(
                title,
                message
            );

            empty.appendChild(
                copy
            );

            this.container.appendChild(
                empty
            );
        }

        /* ==========================================================
           Shopify cart mutation
           ========================================================== */

        async changeLine(
            identifier,
            quantity,
            { optimistic = false } = {}
        ) {
            if (!optimistic) {
                this.setLoading(true);
            }

            this.clearFeedback();

            const controller =
                new AbortController();

            this.activeMutationControllers.add(
                controller
            );

            const payload =
                identifier?.key
                    ? {
                        id: identifier.key,
                        quantity
                    }
                    : {
                        line:
                            identifier?.line,
                        quantity
                    };

            try {
                const response =
                    await fetch(
                        `${this.getShopRoot()}cart/change.js`,
                        {
                            method: 'POST',

                            headers: {
                                'Content-Type':
                                    'application/json',

                                Accept:
                                    'application/json'
                            },

                            body:
                                JSON.stringify(
                                    payload
                                ),

                            signal:
                                controller.signal
                        }
                    );

                const data =
                    await response.json();

                if (!response.ok) {
                    throw data;
                }

                /*
                 * Keep the existing cart event contract so cart.js,
                 * product forms, quick add and already-built integrations
                 * continue to sync.
                 */
                document.dispatchEvent(
                    new CustomEvent(
                        'theme:cart:update',
                        {
                            bubbles: true,

                            detail: {
                                cart: data,
                                source: 'cart-drawer'
                            }
                        }
                    )
                );
            } catch (error) {
                if (
                    error?.name ===
                    'AbortError'
                ) {
                    return;
                }

                console.error(
                    '[Theme Cart Drawer]',
                    error
                );

                const message =
                    error?.description ||
                    error?.message ||
                    this.root?.dataset.cartGeneralError || '';

                this.showFeedback(
                    message
                );

                /*
                 * Reconcile optimistic UI with Shopify if a mutation fails.
                 */
                await this.refreshSection();
            } finally {
                this.activeMutationControllers.delete(
                    controller
                );

                if (!optimistic) {
                    this.setLoading(false);
                }
            }
        }

        /* ==========================================================
           Section Rendering API refresh
           ========================================================== */

        async refreshSection() {
            const sectionId =
                this.root?.dataset.sectionId;

            if (!sectionId) {
                return;
            }

            this.activeRefreshController?.abort();

            const controller =
                new AbortController();

            this.activeRefreshController =
                controller;

            const requestId =
                ++this.refreshSequence;

            const url =
                new URL(
                    window.location.href
                );

            url.searchParams.set(
                'section_id',
                sectionId
            );

            try {
                const response =
                    await fetch(
                        url.toString(),
                        {
                            headers: {
                                Accept:
                                    'text/html'
                            },

                            signal:
                                controller.signal
                        }
                    );

                if (!response.ok) {
                    throw new Error(
                        `Cart drawer refresh failed: ${response.status}`
                    );
                }

                const html =
                    await response.text();

                if (
                    requestId !==
                    this.refreshSequence
                ) {
                    return;
                }

                const doc =
                    new DOMParser()
                        .parseFromString(
                            html,
                            'text/html'
                        );

                const newContainer =
                    doc.querySelector(
                        '[data-cart-drawer-container]'
                    );

                const newRoot =
                    doc.querySelector(
                        '[data-cart-drawer]'
                    );

                if (
                    !newContainer ||
                    !this.container
                ) {
                    throw new Error(
                        'Cart drawer section markup was not found.'
                    );
                }

                this.container.replaceWith(
                    newContainer
                );

                this.container =
                    newContainer;

                this.feedback =
                    newContainer.querySelector(
                        '[data-cart-feedback]'
                    );

                this.bindContainer(
                    newContainer
                );

                const itemCount =
                    Number(
                        newRoot?.dataset.itemCount
                    );

                if (
                    Number.isFinite(
                        itemCount
                    )
                ) {
                    this.updateBadges(
                        itemCount
                    );
                }
            } catch (error) {
                if (
                    error?.name ===
                    'AbortError'
                ) {
                    return;
                }

                console.error(
                    '[Theme Cart Drawer] Section refresh failed',
                    error
                );

                this.showFeedback(
                    this.root?.dataset.cartRefreshError || ''
                );
            } finally {
                if (
                    this.activeRefreshController ===
                    controller
                ) {
                    this.activeRefreshController =
                        null;
                }
            }
        }

        /* ==========================================================
           Counts / badges
           ========================================================== */

        updateBadges(itemCount) {
            const count =
                Math.max(
                    0,
                    Number(itemCount) || 0
                );

            if (this.root) {
                this.root.dataset.itemCount =
                    String(count);
            }

            document
                .querySelectorAll(
                    '[data-cart-count]'
                )
                .forEach((element) => {
                    element.textContent =
                        String(count);

                    element.hidden =
                        count === 0;
                });

            this.container
                ?.querySelectorAll(
                    '.cart-drawer__summary-count'
                )
                .forEach((element) => {
                    element.textContent =
                        String(count);
                });

            document
                .querySelectorAll(
                    TOGGLE_SELECTOR
                )
                .forEach((trigger) => {
                    const template =
                        count === 0
                            ? this.root?.dataset.cartAriaEmpty
                            : count === 1
                                ? this.root?.dataset.cartAriaItem
                                : this.root?.dataset.cartAriaItems;

                    const label =
                        (template || '').replace(
                            '__COUNT__',
                            String(count)
                        );

                    trigger.setAttribute(
                        'aria-label',
                        label
                    );
                });
        }

        /* ==========================================================
           Loading / feedback
           ========================================================== */

        setLoading(isLoading) {
            this.root?.classList.toggle(
                'is-loading',
                isLoading
            );

            this.root?.toggleAttribute(
                'data-loading',
                isLoading
            );

            this.container?.setAttribute(
                'aria-busy',
                String(isLoading)
            );
        }

        showFeedback(message) {
            if (!this.feedback) {
                return;
            }

            this.feedback.textContent =
                message;

            this.feedback.classList.add(
                'is-visible',
                'is-error'
            );
        }

        clearFeedback() {
            if (!this.feedback) {
                return;
            }

            this.feedback.textContent =
                '';

            this.feedback.classList.remove(
                'is-visible',
                'is-error'
            );
        }

        /* ==========================================================
           Keyboard / focus trap
           ========================================================== */

        handleKeydown(event) {
            if (!this.isOpen()) {
                return;
            }

            if (
                event.key ===
                'Escape'
            ) {
                event.preventDefault();

                this.close();

                return;
            }

            if (
                event.key ===
                'Tab'
            ) {
                this.trapFocus(
                    event
                );
            }
        }

        trapFocus(event) {
            if (!this.panel) {
                return;
            }

            const focusable =
                getFocusableElements(
                    this.panel
                );

            if (!focusable.length) {
                event.preventDefault();

                this.panel.focus();

                return;
            }

            const first =
                focusable[0];

            const last =
                focusable[
                focusable.length - 1
                ];

            if (
                !this.panel.contains(
                    document.activeElement
                )
            ) {
                event.preventDefault();

                first.focus();

                return;
            }

            if (
                event.shiftKey &&
                document.activeElement ===
                first
            ) {
                event.preventDefault();

                last.focus();

                return;
            }

            if (
                !event.shiftKey &&
                document.activeElement ===
                last
            ) {
                event.preventDefault();

                first.focus();
            }
        }

        /* ==========================================================
           Cleanup
           ========================================================== */

        destroy() {
            window.clearTimeout(
                this.closeTimer
            );

            this.quantityTimeouts.forEach(
                (timeout) => {
                    window.clearTimeout(
                        timeout
                    );
                }
            );

            this.quantityTimeouts.clear();

            this.activeRefreshController?.abort();

            this.activeMutationControllers.forEach(
                (controller) =>
                    controller.abort()
            );

            this.activeMutationControllers.clear();

            this.containerAbortController?.abort();

            this.abortController.abort();

            this.setBackgroundInert(false);

            if (this.root) {
                this.root.classList.remove(
                    'is-open',
                    'is-loading'
                );

                this.root.removeAttribute(
                    'data-loading'
                );

                this.root.hidden = true;
            }
        }
    }

    /* ============================================================
       Initialization
       ============================================================ */

    const initializeCartDrawer = () => {
        cartDrawerInstance?.destroy();

        const instance =
            new CartDrawer();

        if (!instance.root) {
            cartDrawerInstance = null;

            return;
        }

        cartDrawerInstance =
            instance;
    };

    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            initializeCartDrawer,
            {
                once: true
            }
        );
    } else {
        initializeCartDrawer();
    }

    /* ============================================================
       Shopify Theme Editor
       ============================================================ */

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            if (
                event.target.querySelector?.(
                    '[data-cart-drawer]'
                )
            ) {
                initializeCartDrawer();
            }
        }
    );
})();